import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  input,
  output,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/core';

import moment from 'moment';

import { Observable, Subscription, debounceTime, of, startWith } from 'rxjs';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';

export interface AccountChangeTimezoneOptions {
  currentValue: string;
  defaultValue: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-account-change-timezone',
  templateUrl: './account-change-timezone.component.html',
  styleUrls: ['./account-change-timezone.component.scss'],
  standalone: false,
})
export class AccountChangeTimezoneComponent implements OnInit, AfterViewInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = input<AccountChangeTimezoneOptions>();
  readonly modalClosed = output<string>();
  autoCompleteInput = viewChild('autoCompleteInput', { read: ElementRef });
  pickerOptions = viewChildren(MatOption, { read: ElementRef });

  private readonly timezoneMap = {
    'Asia/Calcutta': 'Asia/Kolkata',
  };
  public timezone: { id: string; label: string };
  timezoneDropdown: { id: string; label: string }[] = [];
  stringFilter = (value, list) =>
    list.filter((p) =>
      p.label.toLowerCase().includes(value.label ? value.label.toLowerCase() : value.toLowerCase())
    );
  pickerControl: FormControl;
  filteredOptions: Observable<any>;
  valueChangeSubscription: Subscription;
  timezoneInvalid = false;

  constructor(
    private timezoneService: TimezoneService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    let timezoneDropdown = [];
    const countries = moment.tz.countries();
    const getCountryNames = new Intl.DisplayNames(['en'], { type: 'region' });
    for (const countryCode of countries) {
      const tempSubmenu = [];
      const countryName = getCountryNames.of(countryCode);
      const countryZones = moment.tz.zonesForCountry(countryCode);
      for (const zone of countryZones) {
        const zoneOffset = this.timezoneService.calculateOffset(zone);
        const formattedZone = zone.substring(zone.indexOf('/') + 1).replace('_', ' ');
        const formattedCity = `${countryName} - ${formattedZone} (GMT ${zoneOffset})`;

        tempSubmenu.push({ id: zone, label: formattedCity });
      }

      timezoneDropdown = timezoneDropdown.concat(tempSubmenu);
    }
    timezoneDropdown.push({ id: 'Etc/UTC', label: 'UTC (GMT +00:00)' });
    this.timezoneDropdown = [...new Map(timezoneDropdown.map((i) => [i['id'], i])).values()];
    this.timezoneDropdown.sort((a, b) => a.label.localeCompare(b.label));
    const mappedCurrentValue =
      this.timezoneMap[this.options().currentValue] ?? this.options().currentValue;

    this.timezone = this.timezoneDropdown.find((i) => i.id === mappedCurrentValue) ?? {
      id: this.options().defaultValue,
      label: this.options().defaultValue,
    };

    this.pickerControl = new FormControl('', Validators.required);
    this.filteredOptions = of(this.timezoneDropdown);
    this.valueChangeSubscription = this.pickerControl.valueChanges
      .pipe((startWith(''), debounceTime(200)))
      .subscribe((value) => {
        this.filteredOptions = of(this.stringFilter(value, this.timezoneDropdown));
        this.checkTimezoneValidity();
      });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      const elem = this.pickerOptions().find((opt) => {
        return opt.nativeElement.id === this.timezone.id;
      });
      elem?.nativeElement.scrollIntoView({ block: 'start', behavior: 'instant' });
    }, 500);
  }

  public cancel(): void {
    this.modalClosed.emit(null);
  }

  public confirm(): void {
    this.modalClosed.emit(this.pickerControl.value.id);
  }

  displayFn(value: any) {
    return value ? value.label : undefined;
  }

  checkTimezoneValidity() {
    this.timezoneInvalid = false;
    if (typeof this.pickerControl.value === 'string') {
      const foundIndex = this.timezoneDropdown
        .map((i) => i.label)
        .indexOf(this.pickerControl.value);
      if (foundIndex !== -1) {
        this.pickerControl.setValue(this.timezoneDropdown[foundIndex]);
      } else {
        this.timezoneInvalid = true;
      }
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.valueChangeSubscription.unsubscribe();
  }
}
